import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';
import AdminNavTabs from '../components/AdminNavTabs';
import './AdminCategories.css';
import './AdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';
const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';

const AdminCategories = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [username, setUsername] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all, root, subcategories
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingAll, setDeletingAll] = useState(false);
  const statusRef = useRef(null);

  const [formData, setFormData] = useState({
    ProductName: '',
    ParentCategoryID: '',
    Description: '',
    ImageURL: '',
  });

  // Lock background scroll while the modal is open
  useEffect(() => {
    if (showForm) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previous;
      };
    }
  }, [showForm]);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(`${API_BASE_URL}/admin/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'Categories');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }
        const user = await Auth.currentAuthenticatedUser();
        setUsername(user.username || '');
        await fetchCategories();
      } catch (err) {
        console.error('Auth error:', err);
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // Scroll to status message
  useEffect(() => {
    if ((error || successMessage) && statusRef.current) {
      statusRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error, successMessage]);

  // Get root categories (for parent dropdown)
  const rootCategories = categories.filter(cat => !cat.ParentCategoryID);

  // Build category hierarchy map
  const categoryMap = {};
  const subcategoryMap = {};
  categories.forEach(cat => {
    categoryMap[cat.ProductID] = cat;
    if (cat.ParentCategoryID) {
      if (!subcategoryMap[cat.ParentCategoryID]) {
        subcategoryMap[cat.ParentCategoryID] = [];
      }
      subcategoryMap[cat.ParentCategoryID].push(cat);
    }
  });

  // Filter and search categories
  const filteredCategories = categories.filter(cat => {
    // Filter by mode
    if (filterMode === 'root' && cat.ParentCategoryID) return false;
    if (filterMode === 'subcategories' && !cat.ParentCategoryID) return false;

    // Search by name
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const name = (cat.ProductName || '').toLowerCase();
      const parentName = cat.ParentCategoryID 
        ? (categoryMap[cat.ParentCategoryID]?.ProductName || '').toLowerCase()
        : '';
      return name.includes(query) || parentName.includes(query);
    }

    return true;
  });

  // Sort categories: root categories first, then subcategories under their parents
  const sortedCategories = [...filteredCategories].sort((a, b) => {
    // If both are root or both are subcategories, sort alphabetically
    if ((!a.ParentCategoryID && !b.ParentCategoryID) || 
        (a.ParentCategoryID && b.ParentCategoryID)) {
      return (a.ProductName || '').localeCompare(b.ProductName || '');
    }
    // Root categories come before subcategories
    return a.ParentCategoryID ? 1 : -1;
  });

  const handleCreateNew = () => {
    setEditingCategory(null);
    setFormData({
      ProductName: '',
      ParentCategoryID: '',
      Description: '',
      ImageURL: '',
    });
    setShowForm(true);
    setError('');
    setSuccessMessage('');
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      ProductName: category.ProductName || '',
      ParentCategoryID: category.ParentCategoryID || '',
      Description: category.Description || '',
      ImageURL: category.ImageURL || '',
    });
    setShowForm(true);
    setError('');
    setSuccessMessage('');
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCategory(null);
    setFormData({
      ProductName: '',
      ParentCategoryID: '',
      Description: '',
      ImageURL: '',
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // Prepare payload
      const payload = {
        ProductName: formData.ProductName.trim(),
        Description: formData.Description.trim(),
        ImageURL: formData.ImageURL.trim(),
      };

      // Handle parent category (empty string means no parent)
      if (formData.ParentCategoryID && formData.ParentCategoryID.trim()) {
        payload.ParentCategoryID = formData.ParentCategoryID.trim();
      } else {
        payload.ParentCategoryID = null;
      }

      const url = editingCategory
        ? `${API_BASE_URL}/admin/categories/${editingCategory.ProductID}`
        : `${API_BASE_URL}/admin/categories`;

      const method = editingCategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(friendlyApiError(response.status, data, 'Failed to save category'));
      }

      setSuccessMessage(
        editingCategory
          ? 'Category updated successfully'
          : 'Category created successfully'
      );
      setShowForm(false);
      setEditingCategory(null);
      setFormData({
        ProductName: '',
        ParentCategoryID: '',
        Description: '',
        ImageURL: '',
      });

      // Refresh categories
      await fetchCategories();
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.message || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (category) => {
    const subcategories = subcategoryMap[category.ProductID] || [];
    const confirmMessage = subcategories.length > 0
      ? `Delete "${category.ProductName}"? Its ${subcategories.length} subcategory(ies) will be promoted to root categories.`
      : `Delete "${category.ProductName}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(
        `${API_BASE_URL}/admin/categories/${category.ProductID}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(friendlyApiError(response.status, data, 'Failed to delete category'));
      }

      setSuccessMessage('Category deleted successfully');
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.message || 'Failed to delete category');
    }
  };

  // Delete ALL categories (destructive). Mirrors the single-delete endpoint,
  // iterating over every loaded category. Products keep their Category name and
  // are simply no longer backed by a category record (same behavior as deleting
  // categories one by one). Requires an explicit confirmation.
  const handleDeleteAll = async () => {
    const total = categories.length;
    if (total === 0 || deletingAll) return;

    const confirmMessage =
      `Delete ALL ${total} categor${total === 1 ? 'y' : 'ies'}? This cannot be undone. ` +
      `Products are not deleted, but they will no longer belong to any category.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeletingAll(true);
    setError('');
    setSuccessMessage('');
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // Snapshot the ids up front so promotion/refresh can't shift the list.
      const ids = categories
        .map((c) => c && c.ProductID)
        .filter(Boolean);

      let okCount = 0;
      let failCount = 0;
      for (const id of ids) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetch(`${API_BASE_URL}/admin/categories/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!response.ok) {
            failCount += 1;
          } else {
            okCount += 1;
          }
        } catch (e) {
          failCount += 1;
        }
      }

      if (failCount === 0) {
        setSuccessMessage(`Deleted all ${okCount} categories.`);
      } else {
        setError(`Deleted ${okCount} of ${ids.length} categories; ${failCount} failed. You can retry.`);
      }
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting all categories:', err);
      setError(err.message || 'Failed to delete all categories');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-sitemap"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>Category Management</h1>
            <p>Organize your product catalog</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-categories-container">
        <div className="admin-categories-header">
          <button
            className="btn-create-category"
            onClick={handleCreateNew}
            disabled={showForm || deletingAll}
          >
            <i className="fas fa-plus"></i> Create Category
          </button>
          <button
            className="btn-delete-all-categories"
            onClick={handleDeleteAll}
            disabled={showForm || deletingAll || categories.length === 0}
            title="Delete all categories"
          >
            <i className={`fas ${deletingAll ? 'fa-spinner fa-spin' : 'fa-trash'}`}></i>{' '}
            {deletingAll ? 'Deleting…' : 'Delete All'}
          </button>
        </div>

        {/* Status Messages */}
        <div ref={statusRef}>
          {error && (
            <div className="alert alert-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}
          {successMessage && (
            <div className="alert alert-success">
              <i className="fas fa-check-circle"></i> {successMessage}
            </div>
          )}
        </div>

        {/* Category Form */}
        {showForm && createPortal(
          <div className="category-form-overlay" onMouseDown={handleCancelForm}>
            <div className="category-form-card" onMouseDown={(e) => e.stopPropagation()}>
              <h2>{editingCategory ? 'Edit Category' : 'Create New Category'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="categoryName">
                    Category Name <span className="required">*</span>
                  </label>
                  <input
                    id="categoryName"
                    type="text"
                    value={formData.ProductName}
                    onChange={(e) =>
                      setFormData({ ...formData, ProductName: e.target.value })
                    }
                    required
                    maxLength={255}
                    placeholder="Enter category name"
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="parentCategory">Parent Category</label>
                  <select
                    id="parentCategory"
                    value={formData.ParentCategoryID}
                    onChange={(e) =>
                      setFormData({ ...formData, ParentCategoryID: e.target.value })
                    }
                    disabled={submitting}
                  >
                    <option value="">None (Root Category)</option>
                    {rootCategories
                      .filter(cat => !editingCategory || cat.ProductID !== editingCategory.ProductID)
                      .map((cat) => (
                        <option key={cat.ProductID} value={cat.ProductID}>
                          {cat.ProductName}
                        </option>
                      ))}
                  </select>
                  <small className="form-help">
                    Select a parent category to create a subcategory. Leave empty for a root category.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={formData.Description}
                    onChange={(e) =>
                      setFormData({ ...formData, Description: e.target.value })
                    }
                    rows={3}
                    placeholder="Enter category description"
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="imageUrl">Image URL</label>
                  <input
                    id="imageUrl"
                    type="url"
                    value={formData.ImageURL}
                    onChange={(e) =>
                      setFormData({ ...formData, ImageURL: e.target.value })
                    }
                    placeholder="https://example.com/image.jpg"
                    disabled={submitting}
                  />
                  {formData.ImageURL && (
                    <div className="image-preview">
                      <img src={formData.ImageURL} alt="Category preview" />
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={handleCancelForm}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={submitting || !formData.ProductName.trim()}
                  >
                    {submitting ? 'Saving...' : editingCategory ? 'Update Category' : 'Create Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* Filters and Search */}
        <div className="category-filters">
          <div className="filter-buttons">
            <button
              className={filterMode === 'all' ? 'active' : ''}
              onClick={() => setFilterMode('all')}
            >
              All Categories ({categories.length})
            </button>
            <button
              className={filterMode === 'root' ? 'active' : ''}
              onClick={() => setFilterMode('root')}
            >
              Root Only ({rootCategories.length})
            </button>
            <button
              className={filterMode === 'subcategories' ? 'active' : ''}
              onClick={() => setFilterMode('subcategories')}
            >
              Subcategories Only ({categories.filter(c => c.ParentCategoryID).length})
            </button>
          </div>

          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Category List */}
        {loading ? (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i> Loading categories...
          </div>
        ) : sortedCategories.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-folder-open"></i>
            <p>No categories found</p>
            {!showForm && (
              <button className="btn-create-category" onClick={handleCreateNew}>
                Create Your First Category
              </button>
            )}
          </div>
        ) : (
          <div className="category-list">
            <table className="category-table">
              <thead>
                <tr>
                  <th>Category Name</th>
                  <th>Parent Category</th>
                  <th>Subcategories</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCategories.map((category) => {
                  const isSubcategory = !!category.ParentCategoryID;
                  const parentCategory = isSubcategory
                    ? categoryMap[category.ParentCategoryID]
                    : null;
                  const subcategoryCount = (subcategoryMap[category.ProductID] || []).length;

                  return (
                    <tr
                      key={category.ProductID}
                      className={isSubcategory ? 'subcategory-row' : 'root-category-row'}
                    >
                      <td className="category-name-cell">
                        {isSubcategory && <span className="indent-marker">└─</span>}
                        {category.ImageURL && (
                          <img
                            src={category.ImageURL}
                            alt={category.ProductName}
                            className="category-thumbnail"
                          />
                        )}
                        <span className="category-name">{category.ProductName}</span>
                      </td>
                      <td className="parent-category-cell">
                        {parentCategory ? (
                          <span className="parent-badge">{parentCategory.ProductName}</span>
                        ) : (
                          <span className="root-badge">Root</span>
                        )}
                      </td>
                      <td className="subcategory-count-cell">
                        {subcategoryCount > 0 ? (
                          <span className="subcategory-badge">
                            {subcategoryCount} {subcategoryCount === 1 ? 'subcategory' : 'subcategories'}
                          </span>
                        ) : (
                          <span className="no-subcategories">—</span>
                        )}
                      </td>
                      <td className="description-cell">
                        {category.Description || <span className="no-description">—</span>}
                      </td>
                      <td className="actions-cell">
                        <button
                          className="btn-edit"
                          onClick={() => handleEdit(category)}
                          title="Edit category"
                          disabled={showForm}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(category)}
                          title="Delete category"
                          disabled={showForm}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCategories;
